'use client';

import localisePath from '@app/lib/helpers/localise-path';
import proxyImageUrlIfEnabled from '@app/lib/images/proxy-image-url-if-enabled';
import type { Language } from '@app/lib/store-context/Context';
import { StaticImage, Video } from 'mmds';
import { useParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import type {
    FeatureScrollLockingSliceDefaultPrimary,
    FeatureScrollLockingSliceDefaultPrimaryFeatureGroupItem,
} from '../../../prismicio-types';
import s from './FeatureScrollLocking.module.css';

type FeatureItem = FeatureScrollLockingSliceDefaultPrimaryFeatureGroupItem;

interface FeatureScrollLockingProps {
    primary: FeatureScrollLockingSliceDefaultPrimary;
}

const FeatureScrollLocking = ({ primary }: FeatureScrollLockingProps) => {
    const { layout, image_border, feature_group } = primary;
    const [activeIndex, setActiveIndex] = useState(0);
    const [contentMinHeight, setContentMinHeight] = useState<
        number | undefined
    >(undefined);
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRefs = useRef<(HTMLParagraphElement | null)[]>([]);
    const params = useParams();
    const lang = params?.lang as Language;

    const numItems = feature_group?.length ?? 0;

    useEffect(() => {
        if (numItems === 0) return;
        const handleScroll = () => {
            const container = containerRef.current;
            if (!container) return;
            const { top, height } = container.getBoundingClientRect();
            const scrollableHeight = height - window.innerHeight;
            if (scrollableHeight <= 0) return;
            const progress = -top / scrollableHeight;
            const nextIndex = Math.min(
                Math.max(Math.floor(progress * numItems), 0),
                numItems - 1
            );
            setActiveIndex((prev) => (prev === nextIndex ? prev : nextIndex));
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [numItems]);

    // Measure all content paragraphs after mount to find the tallest,
    // so the wrapper never shrinks when switching items.
    useEffect(() => {
        const heights = contentRefs.current.map((el) => el?.offsetHeight ?? 0);
        const max = Math.max(0, ...heights);
        if (max > 0) setContentMinHeight(max);
    }, []);

    if (numItems === 0) return null;

    const activeItem: FeatureItem = feature_group[activeIndex];
    const isImageLeft = layout === 'Image Left';
    const borderAttr =
        image_border?.toLowerCase().replace(/\s+/g, '-') ?? 'full';
    const isXLWithCta = borderAttr === 'xl-with-cta';
    const hasAnyCta = feature_group.some(
        (item: FeatureItem) => !!item.cta_label
    );

    const videoDesktopUrl =
        'url' in activeItem.video_desktop
            ? activeItem.video_desktop.url
            : undefined;
    const videoMobileUrl =
        'url' in activeItem.video_mobile
            ? activeItem.video_mobile.url
            : undefined;
    const hasVideo = !!(videoDesktopUrl || videoMobileUrl);

    const ctaContent = activeItem.cta_label ? (
        <a
            href={localisePath(activeItem.cta_link ?? '', lang)}
            className={s.ctaLink}
        >
            <span className={s.ctaLabel}>{activeItem.cta_label}</span>
            {activeItem.cta_icon?.url && (
                <img
                    src={proxyImageUrlIfEnabled(activeItem.cta_icon.url)}
                    alt=""
                    aria-hidden="true"
                    className={s.ctaIcon}
                />
            )}
        </a>
    ) : null;

    return (
        <div ref={containerRef} style={{ height: `${numItems * 100}vh` }}>
            <div className={s.stickyWrapper}>
                <div
                    className={`${s.root} ${
                        isImageLeft ? s.imageLeft : s.imageRight
                    }`}
                >
                    {/* Feature list panel: context + feature content (desktop) + button list */}
                    <div className={s.featureListPanel}>
                        {activeItem.context && (
                            <div className={s.context}>
                                {activeItem.context}
                            </div>
                        )}
                        {/* Desktop only: large feature description — min-height driven by tallest item */}
                        <div
                            className={s.featureContentWrapper}
                            style={
                                contentMinHeight !== undefined
                                    ? { minHeight: contentMinHeight }
                                    : undefined
                            }
                        >
                            {feature_group.map((item, i) => (
                                <p
                                    key={i}
                                    ref={(el) => {
                                        contentRefs.current[i] = el;
                                    }}
                                    className={s.featureContent}
                                    aria-hidden={
                                        i !== activeIndex ? true : undefined
                                    }
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        width: '100%',
                                        visibility:
                                            i === activeIndex
                                                ? 'visible'
                                                : 'hidden',
                                        pointerEvents:
                                            i === activeIndex ? 'auto' : 'none',
                                    }}
                                >
                                    {item.feature_content}
                                </p>
                            ))}
                        </div>
                        {/* Desktop only: CTA right after content for non-xl-with-cta variants */}
                        {!isXLWithCta && hasAnyCta && (
                            <div className={s.ctaWrapper}>
                                {ctaContent ?? (
                                    <span
                                        className={s.ctaLink}
                                        style={{ visibility: 'hidden' }}
                                        aria-hidden="true"
                                    >
                                        <span className={s.ctaLabel}>
                                            &nbsp;
                                        </span>
                                    </span>
                                )}
                            </div>
                        )}
                        <ul className={s.featureList} role="list">
                            {feature_group.map((item, i) => (
                                <li key={i} className={s.featureListItem}>
                                    <span
                                        className={`${s.featureButton} ${
                                            i === activeIndex
                                                ? s.featureButtonActive
                                                : s.featureButtonInactive
                                        }`}
                                    >
                                        <span className={s.featureButtonLabel}>
                                            {item.button_label}
                                        </span>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Image / video panel */}
                    <div className={s.imagePanel}>
                        <div className={s.imageGroup} data-border={borderAttr}>
                            <div
                                className={s.imageWrapper}
                                data-border={borderAttr}
                            >
                                {hasVideo ? (
                                    <Video
                                        desktopVideoUrl={videoDesktopUrl}
                                        mobileVideoUrl={videoMobileUrl}
                                        controls={false}
                                        muted={true}
                                        loop={true}
                                        autoplay={true}
                                    />
                                ) : (
                                    <StaticImage
                                        desktopImage={
                                            activeItem.image_desktop?.url ??
                                            undefined
                                        }
                                        mobileImage={
                                            activeItem.image_mobile?.url ??
                                            undefined
                                        }
                                        altDescription={
                                            activeItem.button_label ?? ''
                                        }
                                        fullWidth
                                        urlTransformer={proxyImageUrlIfEnabled}
                                    />
                                )}
                            </div>
                            {isXLWithCta && (
                                <div className={s.ctaBelowImage}>
                                    {ctaContent}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Feature info: mobile only — two-column: context left, content+CTA right */}
                    <div className={s.featureInfo}>
                        {activeItem.context && (
                            <div className={s.featureLabel}>
                                {activeItem.context}
                            </div>
                        )}
                        <div className={s.featureInfoRight}>
                            {activeItem.feature_content && (
                                <p className={s.featureContentMobile}>
                                    {activeItem.feature_content}
                                </p>
                            )}
                            {!isXLWithCta && hasAnyCta && (
                                <div className={s.ctaWrapper}>
                                    {ctaContent ?? (
                                        <span
                                            className={s.ctaLink}
                                            style={{ visibility: 'hidden' }}
                                            aria-hidden="true"
                                        >
                                            <span className={s.ctaLabel}>
                                                &nbsp;
                                            </span>
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FeatureScrollLocking;
